import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Price from '../Price';
import './ProductCard.css';

/**
 * ProductCard Component
 * 
 * Product display card with image, title, description, price and color swatches.
 * 
 * Figma variants:
 * | Brand | Status  | nodeId       |
 * |-------|---------|--------------|
 * | CLS   | Default | 3676:10559   |
 * | CLS   | Hover   | 3676:10851   |
 * | ABC   | Default | 3676:10660   |
 * | ABC   | Hover   | 3676:29533   |
 * | Overall | Default | 4324:24762 (MCP Version) |
 * | Overall | Hover   | 4324:24848 (MCP Version) |
 * | Crownstudent | Default | 4432:3492 (MCP Version) |
 * | Crownstudent | Hover   | 4432:3534 (MCP Version) |
 * 
 * Brand differences (auto via data-brand on <html>):
 * - CLS: no padding, no border, inner-radius 2px, no shadow
 * - ABC: padding medium-s, border 1px gray-05, radius-card (12px), card shadow
 * - Overall: the image wrap is the card (bleed + frame)
 * - Crownstudent: the Overall frame, gray-975 selected chip
 * 
 * Hover:
 * - CLS: image zoom
 * - ABC: border → success color, shadow changes
 * - Overall: frame turns white + popup shadow, photo pulls in
 * - Crownstudent: frame turns white + popup shadow, photo grows to the edge
 *
 * `frameMark` (an image URL) adds four marks at the middle of each frame edge
 * that fade in on hover — appearance follows the data, any brand can pass it
 * (OVER/LL passes its logotype glyph).
 */
const MARK_SIDES = ['top', 'right', 'bottom', 'left'];

const RATIO_MAP = {
  '1:1': '100%',
  '4:5': '125%',
  '3:4': '133.33%',
  '4:3': '75%',
  '16:9': '56.25%',
  '3:2': '66.67%',
};

const ProductCard = ({
  image,
  imageAlt = '',
  secondImage = '',
  secondImageAlt = '',
  title = 'Product name',
  description = '',
  priceValue = '',
  priceState = 'default',
  originalPrice = '',
  priceBadgeText = 'Included',
  priceSavingsText = '',
  colors = [],
  moreLabel = 'more',
  selected = false,
  selectedLabel = 'Selected',
  imageLabels = ['Image 1', 'Image 2'],
  frameMark,
  discountBadgeText = '',
  imageAspectRatio,
  imageFit = 'cover',
  href,
  onClick,
  className = '',
}) => {
  const gridRef = useRef(null);
  const moreRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(colors.length);

  // --- Second image: mobile swipe state ---
  const hasSecondImage = Boolean(secondImage);
  const [activeSlide, setActiveSlide] = useState(0);
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);

  const handleTouchStart = useCallback((e) => {
    if (!hasSecondImage) return;
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  }, [hasSecondImage]);

  const handleTouchMove = useCallback((e) => {
    if (touchStartX.current === null) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (touchStartX.current === null) return;
    const threshold = 40;
    if (touchDeltaX.current < -threshold && activeSlide === 0) {
      setActiveSlide(1);
    } else if (touchDeltaX.current > threshold && activeSlide === 1) {
      setActiveSlide(0);
    }
    touchStartX.current = null;
    touchDeltaX.current = 0;
  }, [activeSlide]);

  // Measure how many swatches fit in one row alongside the "+N more" badge
  const recalcVisible = useCallback(() => {
    const grid = gridRef.current;
    if (!grid || colors.length === 0) return;

    const gap = parseFloat(getComputedStyle(grid).columnGap) || 2;
    const containerWidth = grid.clientWidth;
    const swatchSize = 12; // swatch width, fixed

    // Max swatches that fit without a "+more" badge
    const maxFull = Math.floor((containerWidth + gap) / (swatchSize + gap));

    if (colors.length <= maxFull) {
      setVisibleCount(colors.length);
    } else {
      // Measure actual "+N more" badge width via hidden ref
      const moreEl = moreRef.current;
      const moreWidth = moreEl ? moreEl.scrollWidth : 36;
      const spaceForSwatches = containerWidth - moreWidth - gap;
      const maxWithMore = Math.floor((spaceForSwatches + gap) / (swatchSize + gap));
      setVisibleCount(Math.max(0, maxWithMore));
    }
    // moreLabel: read indirectly via the hidden measurer's DOM width (moreRef)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colors.length, moreLabel]);

  useEffect(() => {
    recalcVisible();
    const grid = gridRef.current;
    if (!grid) return;
    const ro = new ResizeObserver(recalcVisible);
    ro.observe(grid);
    return () => ro.disconnect();
  }, [recalcVisible]);

  const visibleColors = colors.slice(0, visibleCount);
  const hiddenCount = colors.length - visibleCount;

  const rootClasses = [
    'ds-product-card',
    selected && 'ds-product-card--selected',
    className,
  ].filter(Boolean).join(' ');

  const Tag = href ? 'a' : 'div';
  const linkProps = href ? { href } : {};
  // href + onClick: a crawlable link whose plain click still runs the page's own
  // navigation (onClick); Ctrl/Cmd/Shift/Alt clicks open the href like any link.
  // onClick runs BEFORE the default is cancelled: handlers that route the click
  // themselves (OVER/LL useAppLink) skip events that are already defaultPrevented.
  const handleClick = href && onClick
    ? (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      onClick(e);
      if (!e.defaultPrevented) e.preventDefault();
    }
    : onClick;

  return (
    <Tag
      className={rootClasses}
      onClick={handleClick}
      role={onClick && !href ? 'button' : undefined}
      tabIndex={onClick && !href ? 0 : undefined}
      {...linkProps}
    >
      {/* Image container */}
      <div
        className={`ds-product-card__image-wrap${hasSecondImage ? ' ds-product-card__image-wrap--dual' : ''}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="ds-product-card__image-ratio"
          style={imageAspectRatio ? { '--card-image-ratio': RATIO_MAP[imageAspectRatio] || imageAspectRatio } : undefined}
        >
          {/* Primary image */}
          {image && (
            <img
              src={image}
              alt={imageAlt}
              className={`ds-product-card__image ds-product-card__image--primary${imageFit === 'contain' ? ' ds-product-card__image--contain' : ''}${activeSlide === 1 ? ' ds-product-card__image--mobile-hidden' : ''}`}
              loading="lazy"
            />
          )}
          {/* Secondary image — desktop: shows on hover; mobile: shows on swipe */}
          {hasSecondImage && (
            <img
              src={secondImage}
              alt={secondImageAlt || imageAlt}
              className={`ds-product-card__image ds-product-card__image--secondary${imageFit === 'contain' ? ' ds-product-card__image--contain' : ''}${activeSlide === 1 ? ' ds-product-card__image--mobile-visible' : ''}`}
              loading="lazy"
            />
          )}
        </div>

        {/* Mobile bullet indicators */}
        {hasSecondImage && (
          <div className="ds-product-card__bullets">
            <button
              type="button"
              className={`ds-product-card__bullet${activeSlide === 0 ? ' ds-product-card__bullet--active' : ''}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActiveSlide(0); }}
              aria-label={imageLabels[0]}
            />
            <button
              type="button"
              className={`ds-product-card__bullet${activeSlide === 1 ? ' ds-product-card__bullet--active' : ''}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActiveSlide(1); }}
              aria-label={imageLabels[1]}
            />
          </div>
        )}

        {/* Frame marks at the middle of each edge — only when the card gets one */}
        {frameMark && (
          /* The glyph is a CSS mask painted with the mark colour: an <img>
             cannot take `currentColor` (inside an image document it is black) */
          <span className="ds-product-card__marks" aria-hidden="true" style={{ '--ds-product-card-mark': `url("${frameMark}")` }}>
            {MARK_SIDES.map((side) => (
              <span key={side} className={`ds-product-card__mark ds-product-card__mark--${side}`} />
            ))}
          </span>
        )}
      </div>

      {/* Discount badge — top-left corner (the FI club-shop badge style), only
          while the card's price is the variation's discount (see
          services/discountBadge.js) */}
      {discountBadgeText && (
        <div className="ds-product-card__discount-badge">
          <span className="ds-product-card__discount-badge-text">{discountBadgeText}</span>
        </div>
      )}

      {/* Selected badge — positioned relative to card root, not image */}
      {selected && (
        <div className="ds-product-card__badge">
          <span className="ds-product-card__badge-text">
            {selectedLabel}
          </span>
        </div>
      )}

      {/* Content */}
      <div className="ds-product-card__content">
        {/* Title */}
        {title && (
          <p className="ds-product-card__title">
            {title}
          </p>
        )}

        {/* Description */}
        {description && (
          <p className="ds-product-card__description">
            {description}
          </p>
        )}

        {/* Price — delegates to Price component */}
        {priceValue && (
          <div className="ds-product-card__price">
            <Price
              type="small"
              state={priceState}
              price={priceValue}
              originalPrice={originalPrice}
              badgeText={priceBadgeText}
              savingsText={priceSavingsText}
            />
          </div>
        )}

        {/* Color swatches — single row, auto-truncated */}
        {colors.length > 0 && (
          <div className="ds-product-card__colors">
            {/* Hidden measurer for "+N more" badge width */}
            <span
              ref={moreRef}
              className="ds-product-card__color-more"
              aria-hidden="true"
              style={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none' }}
            >
              +{colors.length} {moreLabel}
            </span>
            <div className="ds-product-card__color-grid" ref={gridRef}>
              {visibleColors.map((color, index) => (
                <span
                  key={index}
                  className="ds-product-card__color-swatch"
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
              {hiddenCount > 0 && (
                <span className="ds-product-card__color-more">
                  +{hiddenCount} {moreLabel}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </Tag>
  );
};

ProductCard.propTypes = {
  /** Product image URL */
  image: PropTypes.string,
  /** Alt text for image */
  imageAlt: PropTypes.string,
  /** Second product image URL (hover swap on desktop, swipeable on mobile) */
  secondImage: PropTypes.string,
  /** Alt text for second image */
  secondImageAlt: PropTypes.string,
  /** Product title (uppercase) */
  title: PropTypes.string,
  /** Short description (e.g. "Small") */
  description: PropTypes.string,
  /** Price text (e.g. "159,-") — passed to Price component */
  priceValue: PropTypes.string,
  /** Price display state */
  priceState: PropTypes.oneOf(['default', 'discounted', 'included']),
  /** Original price for discounted state (strikethrough) */
  originalPrice: PropTypes.string,
  /** Badge text for included state */
  priceBadgeText: PropTypes.string,
  /** Savings text for package discounted */
  priceSavingsText: PropTypes.string,
  /** Array of hex color strings for swatches */
  colors: PropTypes.arrayOf(PropTypes.string),
  /** Localized word for the "+N more" swatch-overflow badge (e.g. "flere", "fler", "lisää") */
  moreLabel: PropTypes.string,
  /** Show "selected" badge */
  selected: PropTypes.bool,
  /** Badge label — pass the brand's word ("VALGT", "VALD", "VALITTU"); "Selected" by default */
  selectedLabel: PropTypes.string,
  /** Accessible names of the two image bullets (mobile swipe) */
  imageLabels: PropTypes.arrayOf(PropTypes.string),
  /** Image (URL) drawn at the middle of each frame edge, fading in on hover — e.g. a brand glyph; no marks without it */
  frameMark: PropTypes.string,
  /** Localized discount badge (e.g. "-20% ALENNUS"); shown top-left while set */
  discountBadgeText: PropTypes.string,
  /** Image aspect ratio override. Supported: '1:1', '4:5', '3:4', '4:3', '16:9', '3:2', or raw CSS percentage. Default: 4:5 (125%) */
  imageAspectRatio: PropTypes.string,
  /** How the picture fills its frame: 'cover' (default — a photo, cropped to the frame) or 'contain' (all of it, e.g. a
      cut-out product render on the frame's background) */
  imageFit: PropTypes.oneOf(['cover', 'contain']),
  /** Link URL (makes card a link) */
  href: PropTypes.string,
  /** Click handler; with `href` it replaces the plain left click (modified clicks follow the href) */
  onClick: PropTypes.func,
  /** Additional CSS class */
  className: PropTypes.string,
};

export default ProductCard;
